<script setup lang="ts">
import IconRestore from '~icons/mdi/restore'
import IconPill from '~icons/mdi/pill'
import IconNeedle from '~icons/mdi/needle'
import IconPetFood from '~icons/mdi/food-apple-outline'
import IconOfficeSupplies from '~icons/mdi/cube-outline'
import IconClinic from '~icons/mdi/office-building-marker-outline'
import IconClinicChain from '~icons/mdi/paw'
import IconCampaigns from '~icons/mdi/bullhorn-outline'

const route = useRoute()
const orderHistory = useOrderHistoryStore()
const userStore = useUserStore()
const categoryMenuItems = useCategoryMenuItems()
const localePath = useLocalePath()

const categoryIcons: Record<string, { icon: Component, size: number }> = {
  'Pharma': { icon: IconPill, size: 20 },
  'Consumables': { icon: IconNeedle, size: 20 },
  'Pet Food': { icon: IconPetFood, size: 18 },
  'Office Supplies': { icon: IconOfficeSupplies, size: 18 },
}

const iconByNormalizedTerm = new Map(
  Object.entries(categoryIcons).map(([enTerm, config]) => [enTerm.toLowerCase(), config]),
)

const categoryNavItems = computed(() => categoryMenuItems.value.map((cat) => {
  const normalizedTerm = cat.enTerm.toLowerCase()
  return {
    label: cat.label,
    to: cat.to,
    icon: iconByNormalizedTerm.get(normalizedTerm)?.icon,
    iconSize: iconByNormalizedTerm.get(normalizedTerm)?.size ?? 20,
    navId: `nav_${normalizedTerm.replace(/[^a-z0-9]+/g, '_')}`,
  }
}))

const campaignsTo = computed(() => localePath('/campaigns'))

function isItemActive(to: string, extraPrefixes: string[] = []) {
  return [to, ...extraPrefixes].some(prefix => (
    route.path === prefix || route.path.startsWith(`${prefix}/`)
  ))
}

function itemClass(to: string, extra?: { spaced?: boolean, prefixes?: string[] }) {
  return {
    'ms-2': extra?.spaced,
    'nav-bar__item--active': isItemActive(to, extra?.prefixes),
  }
}

function labelClass(to: string, isBold?: boolean, prefixes?: string[]) {
  return isBold || isItemActive(to, prefixes) ? 'font-weight-bold' : 'font-weight-regular'
}
</script>

<template>
  <div class="nav-bar d-none d-md-flex align-center bg-white">
    <v-container class="d-flex justify-space-between align-center pa-0 h-100">
      <div class="d-flex align-center overflow-x-auto">
        <v-btn
          v-if="orderHistory.totalOrders > 0"
          variant="text"
          color="black"
          rounded="0"
          :to="localePath('/order-history')"
          class="text-none text-body-1 px-2 nav-bar__item"
          :class="itemClass(localePath('/order-history'))"
          data-nav-id="nav_order_history"
        >
          <v-icon size="20" class="mr-1">
            <IconRestore />
          </v-icon>
          <span :class="labelClass(localePath('/order-history'), true)">
            {{ $t('orderHistory.title') }}
          </span>
        </v-btn>

        <v-divider
          v-if="userStore.data?.clinicChain && orderHistory.totalOrders > 0"
          vertical
          color="vm-grey-6"
          thickness="2"
          class="mx-2 align-self-center border-opacity-100 nav-bar__divider"
        />
        <v-btn
          v-if="userStore.data?.clinicChain"
          variant="text"
          color="black"
          rounded="0"
          :to="localePath('/clinic-chain')"
          class="text-none text-body-1 px-2 nav-bar__item"
          :class="itemClass(localePath('/clinic-chain'))"
          data-nav-id="nav_clinic_chain"
        >
          <v-icon size="20" class="mr-1 d-none d-lg-inline-flex">
            <IconClinicChain />
          </v-icon>
          <span :class="labelClass(localePath('/clinic-chain'), true)">
            {{ userStore.data.clinicChain.name }}
          </span>
        </v-btn>

        <v-divider
          v-if="categoryNavItems.length > 0 && (orderHistory.totalOrders > 0 || userStore.data?.clinicChain)"
          vertical
          color="vm-grey-6"
          thickness="2"
          class="mx-2 align-self-center border-opacity-100 nav-bar__divider"
        />
        <v-btn
          v-for="(category, index) in categoryNavItems"
          :key="category.to"
          variant="text"
          color="black"
          rounded="0"
          :to="category.to"
          class="text-none text-body-1 px-2 nav-bar__item"
          :class="itemClass(category.to, { spaced: index > 0 })"
          :data-nav-id="category.navId"
        >
          <v-icon
            v-if="category.icon"
            :size="category.iconSize"
            class="mr-1 d-none d-lg-inline-flex"
          >
            <component :is="category.icon" />
          </v-icon>
          <span :class="labelClass(category.to)">
            {{ category.label }}
          </span>
        </v-btn>

        <v-divider
          v-if="orderHistory.totalOrders > 0 || userStore.data?.clinicChain || categoryNavItems.length > 0"
          vertical
          color="vm-grey-6"
          thickness="2"
          class="mx-2 align-self-center border-opacity-100 nav-bar__divider"
        />
        <v-btn
          variant="text"
          color="black"
          rounded="0"
          :to="localePath('/suppliers')"
          class="text-none text-body-1 px-2 nav-bar__item"
          :class="itemClass(localePath('/suppliers'), { prefixes: [localePath('/supplier')] })"
          data-nav-id="nav_suppliers"
        >
          <v-icon size="16" class="mr-1 d-none d-lg-inline-flex">
            <IconClinic />
          </v-icon>
          <span :class="labelClass(localePath('/suppliers'), true, [localePath('/supplier')])">
            {{ $t('layout.menuSecondary.suppliers') }}
          </span>
        </v-btn>
      </div>

      <v-btn
        :to="campaignsTo"
        variant="flat"
        rounded="lg"
        color="red-50"
        :ripple="false"
        height="40"
        min-width="40"
        class="text-none text-body-1 font-weight-bold text-black flex-shrink-0 nav-bar__item"
        :class="{ 'nav-bar__item--active': isItemActive(campaignsTo, [localePath('/campaign')]) }"
        data-nav-id="nav_campaigns"
      >
        <v-icon
          :icon="IconCampaigns"
          size="20"
          class="mr-lg-2 nav-bar__campaigns-icon"
        />
        <span class="d-none d-lg-inline">
          {{ $t('layout.menuSecondary.campaigns') }}
        </span>
      </v-btn>
    </v-container>
  </div>
</template>

<style scoped>
.nav-bar {
  height: 56px;
  position: sticky;
  top: 80px;
  z-index: 100;
  border-bottom: 1px solid #fdeef6;
  border-width: 2px;
}

.nav-bar__divider {
  height: 20px;
}

.nav-bar__campaigns-icon {
  transform: rotate(-15deg);
}

.nav-bar :deep(.nav-bar__item.v-btn--active > .v-btn__overlay) {
  opacity: 0;
}

.nav-bar :deep(.nav-bar__item--active) {
  color: rgb(var(--v-theme-red-600)) !important;
  box-shadow: inset 0 -3px 0 0 rgb(var(--v-theme-red-600));
}
</style>
